import {useIsTestBooked} from '@hconnect/tuc-processes/fields/testing/useIsTestBooked'
import {Box, Typography} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {Banner} from '../../../../../orderIntake/components/shared/Banner'

/**
 * Props for the MaterialTestingField component
 */
interface MaterialTestingFieldProps {
  /**
   * Custom title for the section
   */
  title?: string
}

/**
 * Reusable Material Testing Field component for RMC orders
 * Uses the same layout as OrderConfirmationSection with Yes/No options
 */
export const MaterialTestingField = ({title}: MaterialTestingFieldProps): JSX.Element => {
  const {t} = useTranslation()
  const {isTestBooked, setIsTestBooked} = useIsTestBooked()

  const defaultTitle = t('orderIntake.rmc.steps.shared.materialTesting.label')
  const displayTitle = title || defaultTitle

  return (
    <Box data-test-id="material-testing-section">
      <Typography
        sx={{
          fontFamily: 'Inter',
          fontWeight: 600,
          fontSize: '18px',
          lineHeight: '24px',
          color: '#29333D',
          marginBottom: '20px'
        }}
      >
        {displayTitle}
      </Typography>

      {/* Radio Button Selection */}
      <Box sx={{display: 'flex', gap: '16px'}}>
        {/* Yes Option */}
        <Box
          onClick={() => setIsTestBooked(true)}
          data-test-id="material-testing-toggle-yes"
          sx={{
            flex: 1,
            padding: '12px 16px',
            border: isTestBooked ? '1px solid #016AD4' : '1px solid #E8ECF0',
            borderRadius: '8px',
            backgroundColor: isTestBooked ? '#E6F0FB' : '#FFFFFF',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Box
            sx={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              border: isTestBooked ? '1px solid #016AD4' : '1px solid #D1D9E0',
              backgroundColor: isTestBooked ? '#016AD4' : '#FFFFFF',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isTestBooked && (
              <Box
                sx={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: '#FFFFFF'
                }}
              />
            )}
          </Box>
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '24px',
              color: '#29333D'
            }}
          >
            {t('orderIntake.rmc.steps.shared.materialTesting.yes')}
          </Typography>
        </Box>

        {/* No Option */}
        <Box
          onClick={() => setIsTestBooked(false)}
          data-test-id="material-testing-toggle-no"
          sx={{
            flex: 1,
            padding: '12px 16px',
            border: !isTestBooked ? '1px solid #F59E0B' : '1px solid #E8ECF0',
            borderRadius: '8px',
            backgroundColor: !isTestBooked ? '#FEF7ED' : '#FFFFFF',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Box
            sx={{
              width: '16px',
              height: '16px',
              borderRadius: '50%',
              border: !isTestBooked ? '1px solid #F59E0B' : '1px solid #D1D9E0',
              backgroundColor: !isTestBooked ? '#F59E0B' : '#FFFFFF',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {!isTestBooked && (
              <Box
                sx={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: '#FFFFFF'
                }}
              />
            )}
          </Box>
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '24px',
              color: '#29333D'
            }}
          >
            {t('orderIntake.rmc.steps.shared.materialTesting.no')}
          </Typography>
        </Box>
      </Box>

      {/* Info Banner - shown when "Yes" is selected */}
      {isTestBooked && (
        <Banner
          variant="info"
          message={t('orderIntake.rmc.steps.shared.materialTesting.infoMessage')}
        />
      )}
    </Box>
  )
}
